import React from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface DeleteObjectModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    objectName: string;
    isFolder?: boolean;
}

const DeleteObjectModal: React.FC<DeleteObjectModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    objectName,
    isFolder = false,
}) => {
    if (!isOpen) return null;

    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    const modalContent = (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-[9999]">
            <div className="bg-gray-900 rounded-xl shadow-xl max-w-md w-full mx-4 border border-gray-800" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-500/20 rounded-lg">
                            <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-white">
                            Delete {isFolder ? 'Folder' : 'Object'}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-300 transition-colors"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-6 py-4">
                    <p className="text-gray-400 mb-4">
                        Are you sure you want to delete{' '}
                        <span className="font-semibold text-white">"{objectName}"</span>?
                    </p>

                    {isFolder && (
                        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-4">
                            <p className="text-sm text-amber-400">
                                <strong>Warning:</strong> This will delete all objects inside this folder.
                            </p>
                        </div>
                    )}

                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                        <p className="text-sm text-red-400">
                            <strong>This action cannot be undone.</strong> The {isFolder ? 'folder and its contents' : 'object'} will be permanently removed from the bucket.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-800 rounded-b-xl">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                    >
                        Delete {isFolder ? 'Folder' : 'Object'}
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default DeleteObjectModal;
